import { useDeviceMode } from './useDeviceMode';

export type GlassMode = 'desktop' | 'pwa' | 'none';

export interface GlassClasses {
  card: string;
  cardBg: string;
  cardBorder: string;
  panel: string;
  modal: string;
  header: string;
  sidebar: string;
  bottomNav: string;
  button: string;
  input: string;
  background: string;
}

export interface GlassBlur {
  sm: string;
  md: string;
  lg: string;
}

export interface GlassOpacity {
  bg: string;
  border: string;
  hover: string;
}

export interface UseGlassmorphismReturn {
  mode: GlassMode;
  glass: GlassClasses;
  blur: GlassBlur;
  opacity: GlassOpacity;
  isGlassEnabled: boolean;
}

/**
 * Hook che restituisce classi CSS glassmorphism appropriate
 * per Desktop (Full Glass 90%) o PWA (Glass leggero 10%)
 * 
 * Desktop: blur forte (20-40px), animazioni fluide, effetti premium
 * PWA: blur leggero (4-12px), NO animazioni continue, risparmio batteria
 */
export function useGlassmorphism(): UseGlassmorphismReturn {
  const { isDesktop, isPWA } = useDeviceMode();
  
  const mode: GlassMode = isDesktop ? 'desktop' : isPWA ? 'pwa' : 'none';
  const isGlassEnabled = isDesktop || isPWA;

  // DESKTOP GLASSMORPHISM - Full Glass (90% uso)
  // Blur forte, animazioni fluide, effetti premium
  if (isDesktop) {
    return {
      mode: 'desktop',
      isGlassEnabled: true,
      glass: {
        card: 'glass-d-card',
        cardBg: 'glass-d-card-bg',
        cardBorder: 'glass-d-card-border',
        panel: 'glass-d-panel',
        modal: 'glass-d-modal',
        header: 'glass-d-header',
        sidebar: 'glass-d-sidebar',
        bottomNav: '',
        button: 'glass-d-button',
        input: 'glass-d-input',
        background: 'glass-d-bg',
      },
      blur: {
        sm: 'blur-d-sm',
        md: 'blur-d-md',
        lg: 'blur-d-lg',
      },
      opacity: {
        bg: 'bg-white/10',
        border: 'border-white/30',
        hover: 'hover:bg-white/20',
      },
    };
  }

  // PWA GLASSMORPHISM - Glass leggero (10% uso)
  // Blur ridotto, NO animazioni continue, risparmio batteria
  if (isPWA) {
    return {
      mode: 'pwa',
      isGlassEnabled: true,
      glass: {
        card: 'glass-p-card',
        cardBg: 'glass-p-card-bg',
        cardBorder: 'glass-p-card-border',
        panel: 'glass-p-panel',
        modal: 'glass-p-modal',
        header: 'glass-p-header',
        sidebar: '',
        bottomNav: 'glass-p-bottom-nav',
        button: 'glass-p-button',
        input: 'glass-p-input',
        background: 'glass-p-bg',
      },
      blur: {
        sm: 'blur-p-sm',
        md: 'blur-p-md',
        lg: 'blur-p-lg',
      },
      opacity: {
        bg: 'bg-white/50',
        border: 'border-white/50',
        hover: 'hover:bg-white/70',
      },
    };
  }

  // Mobile Web - Fallback normale (no glass)
  return {
    mode: 'none',
    isGlassEnabled: false,
    glass: {
      card: 'bg-white shadow-md rounded-lg',
      cardBg: 'bg-white',
      cardBorder: 'border-gray-200',
      panel: 'bg-white shadow-lg rounded-xl',
      modal: 'bg-white shadow-2xl rounded-t-2xl',
      header: 'bg-white border-b border-gray-200',
      sidebar: '',
      bottomNav: 'bg-white border-t border-gray-200',
      button: 'bg-blue-600 text-white rounded-lg',
      input: 'bg-gray-50 border border-gray-300 rounded-lg',
      background: 'bg-gray-50',
    },
    blur: {
      sm: '',
      md: '',
      lg: '',
    },
    opacity: {
      bg: 'bg-white',
      border: 'border-gray-200',
      hover: 'hover:bg-gray-50',
    },
  };
}

export default useGlassmorphism;
